import { useDraggable } from '@dnd-kit/core';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { CSSProperties } from 'react';

import { useStores } from '~store';
import { dateFormat } from '~utils/dateTime';

import { JOB_WIDTH } from './constants';
import { JOB_CARD_INNER_GUTTER_SIZE } from './JobCardActionButton';

interface OrderCardProps {
  orderId: string;
  style?: CSSProperties;
}

export const OrderCard = observer(({ orderId, style }: OrderCardProps) => {
  const theme = useTheme();
  const { driverSchedulerStore } = useStores();

  const group = driverSchedulerStore.unassignedOrderGroups.find(
    (g) => g.order.id === orderId,
  );
  const firstJob = group?.jobs[0];

  // Mirrors JobCard: the backend reports an assigned job as non-assignable
  // whatever the company, so this only isolates entitlement while there is no
  // assignee.
  const firstJobHasNoAssignee =
    !firstJob?.driver && !firstJob?.vendorJobAssignment?.vendorAccount;
  const actingCompanyCannotDispatchJob =
    firstJobHasNoAssignee && firstJob?.driverOrVendorAssignable === false;

  // An order's jobs can span pages, so until every page is in memory the
  // grouping can hand this card a job that is not the order's earliest — and
  // that is the job a drop would assign.
  const orderGroupingIsProvisional = !driverSchedulerStore.isUnassignedJobsFullyLoaded;

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: orderId,
    data: { type: 'order' },
    disabled: actingCompanyCannotDispatchJob || orderGroupingIsProvisional,
  });

  if (!group) return null;

  const { order, jobs } = group;
  const count = jobs.length;

  const orderName =
    order.name ?? order.dispatchNumber?.name ?? order.orderId ?? order.id.slice(0, 8);
  const customerName = order.account.name;

  const pickUpRaw = firstJob?.pickUpWayPoints?.[0]?.site?.name ?? null;
  const dropOffRaw = firstJob?.dropOffWayPoints?.[0]?.site?.name ?? null;
  const pickUpSite = pickUpRaw?.split(',')[0] ?? '-';
  const dropOffSite = dropOffRaw?.split(',')[0] ?? '-';

  const startTime = firstJob?.jobStartAt
    ? dateFormat(firstJob.jobStartAt, 'h:mm A')
    : null;

  const draggableStyle: CSSProperties = {
    ...style,
    width: `${JOB_WIDTH}px`,
    left: '4px',
    ...(transform && {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    }),
  };

  return (
    <Box
      ref={setNodeRef}
      style={draggableStyle}
      {...listeners}
      {...attributes}
      aria-label="order-card"
    >
      <Box
        sx={{
          backgroundColor: theme.semantic.surfaceRaised,
          border: `1px solid ${theme.brandV2.colors.treadGray7}`,
          borderRadius: '12px',
          boxShadow: '0px 1px 6px 0px rgba(0, 0, 0, 0.20)',
          cursor: 'grab',
          p: JOB_CARD_INNER_GUTTER_SIZE,
          position: 'relative',
          transition: 'background 0.2s',
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'grid',
            gap: 1,
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
            <Tooltip
              title={t('dispatch.drivers.unassigned_card_label_order', {
                value: orderName,
              })}
            >
              <Typography
                sx={{
                  color: theme.brandV2.colors.treadBlack,
                  fontSize: '12px',
                  fontWeight: 600,
                  lineHeight: 1.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {orderName}
              </Typography>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Tooltip
              title={t('dispatch.drivers.unassigned_count', { count })}
              placement="top"
            >
              <Box
                sx={{
                  alignItems: 'center',
                  backgroundColor: theme.brandV2.colors.treadOrange,
                  borderRadius: 999,
                  color: 'common.white',
                  display: 'inline-flex',
                  fontSize: '11px',
                  fontWeight: 700,
                  justifyContent: 'center',
                  lineHeight: 1.25,
                  minWidth: 22,
                  px: 0.85,
                  py: 0.15,
                }}
              >
                {count}
              </Box>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 0.5,
            minWidth: 0,
            mt: 0.25,
            overflow: 'hidden',
          }}
        >
          <Tooltip
            title={t('dispatch.drivers.unassigned_card_label_pickup', {
              value: pickUpRaw ?? pickUpSite,
            })}
          >
            <Typography
              component="span"
              sx={{
                color: theme.brandV2.colors.treadGray2,
                flex: '1 1 0',
                fontSize: '12px',
                lineHeight: 1.25,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {pickUpSite}
            </Typography>
          </Tooltip>
          <Typography
            component="span"
            sx={{
              color: theme.brandV2.colors.treadGray3,
              fontSize: '12px',
              flexShrink: 0,
              lineHeight: 1.25,
            }}
          >
            {t('common.to')}
          </Typography>
          <Tooltip
            title={t('dispatch.drivers.unassigned_card_label_dropoff', {
              value: dropOffRaw ?? dropOffSite,
            })}
          >
            <Typography
              component="span"
              sx={{
                color: theme.brandV2.colors.treadGray2,
                flex: '1 1 0',
                fontSize: '12px',
                lineHeight: 1.25,
                minWidth: 0,
                overflow: 'hidden',
                textAlign: 'right',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {dropOffSite}
            </Typography>
          </Tooltip>
        </Box>

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 0.5,
            mt: 0.25,
            overflow: 'hidden',
          }}
        >
          {startTime && (
            <Tooltip
              title={t('dispatch.drivers.unassigned_card_label_start_time', {
                value: startTime,
              })}
            >
              <Typography
                component="span"
                sx={{
                  color: theme.brandV2.colors.treadGray3,
                  fontSize: '11px',
                  fontWeight: 600,
                  lineHeight: 1.25,
                  whiteSpace: 'nowrap',
                }}
              >
                {startTime}
              </Typography>
            </Tooltip>
          )}
          {startTime && customerName && (
            <Typography
              component="span"
              sx={{
                color: theme.brandV2.colors.treadGray4,
                fontSize: '11px',
                lineHeight: 1.25,
              }}
            >
              •
            </Typography>
          )}
          {customerName && (
            <Tooltip
              title={t('dispatch.drivers.unassigned_card_label_customer', {
                value: customerName,
              })}
            >
              <Typography
                component="span"
                sx={{
                  color: theme.brandV2.colors.treadGray3,
                  fontSize: '11px',
                  lineHeight: 1.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {customerName}
              </Typography>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
});
